import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Plus, Edit2, Trash2, Play, Save, X } from 'lucide-react'

interface Template {
  id: string
  name: string
  systemPrompt: string
  description?: string
  createdAt: string
  updatedAt: string
}

interface TemplateManagerProps {
  templates: Template[]
  onSave: (template: Template) => void
  onDelete: (id: string) => void
  onStartSession: (template: Template) => void
}

export function TemplateManager({
  templates,
  onSave,
  onDelete,
  onStartSession
}: TemplateManagerProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  
  const handleNewTemplate = () => {
    setEditingTemplate(null)
    setName('')
    setDescription('')
    setSystemPrompt('あなたはテキストアドベンチャーゲームのゲームマスターです。\nプレイヤーの行動に対して創造的で面白い反応を返してください。')
    setIsEditing(true)
  }
  
  const handleEdit = (template: Template) => {
    setEditingTemplate(template)
    setName(template.name)
    setDescription(template.description || '')
    setSystemPrompt(template.systemPrompt)
    setIsEditing(true)
  }
  
  const handleSave = () => {
    if (!name.trim() || !systemPrompt.trim()) {
      alert('テンプレート名とシステムプロンプトは必須です')
      return
    }
    
    const template: Template = {
      id: editingTemplate?.id || `template_${Date.now()}`,
      name,
      systemPrompt,
      description: description || undefined,
      createdAt: editingTemplate?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    onSave(template)
    setIsEditing(false)
    setEditingTemplate(null)
  }
  
  const handleCancel = () => {
    setIsEditing(false)
    setEditingTemplate(null)
    setName('')
    setDescription('')
    setSystemPrompt('')
  }
  
  const exampleTemplates = [
    {
      name: 'ファンタジーRPG',
      description: '剣と魔法の世界での冒険',
      systemPrompt: 'あなたはファンタジー世界のゲームマスターです。\n魔法、モンスター、ダンジョンなどの要素を含む世界で、プレイヤーの冒険を導いてください。\n戦闘、探索、謎解きなどバランスよく配置し、プレイヤーの選択に応じて物語を展開させてください。'
    },
    {
      name: 'ホラーサバイバル',
      description: '恐怖と生存の駆け引き',
      systemPrompt: 'あなたは恐怖に満ちた世界のゲームマスターです。\nプレイヤーは未知の恐怖から生き延びなければなりません。\n雰囲気を重視し、緊張感のある描写でプレイヤーを恐怖に陥れてください。\nただし、フィクションとして楽しめる範囲に留めてください。'
    },
    {
      name: 'SF宇宙探査',
      description: '宇宙を舞台にした冒険',
      systemPrompt: 'あなたは宇宙探査ゲームのゲームマスターです。\n広大な宇宙を舞台に、未知の惑星、異星人、宇宙船などを扱います。\n科学的な要素と想像力豊かな設定を組み合わせて、壮大な宇宙の物語を展開してください。'
    }
  ]
  
  if (isEditing) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              {editingTemplate ? 'テンプレート編集' : '新規テンプレート'}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="template-name">テンプレート名 *</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: ファンタジーRPG"
              />
            </div>
            
            <div>
              <Label htmlFor="template-description">説明</Label>
              <Input
                id="template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="テンプレートの簡単な説明"
              />
            </div>
            
            <div>
              <Label htmlFor="system-prompt">
                システムプロンプト *
                <span className="text-xs text-gray-500 ml-2">
                  ゲームマスターとしての振る舞いを定義
                </span>
              </Label>
              <Textarea
                id="system-prompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="ゲームマスターとしての振る舞いを詳細に記述..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            
            <div className="space-y-2">
              <Label>参考例</Label>
              <div className="space-y-2">
                {exampleTemplates.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setName(example.name)
                      setDescription(example.description)
                      setSystemPrompt(example.systemPrompt)
                    }}
                    className="text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors w-full"
                  >
                    <div className="font-medium text-sm">{example.name}</div>
                    <div className="text-xs text-gray-600">{example.description}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">
                <Save className="w-4 h-4 mr-2" />
                保存
              </Button>
              <Button onClick={handleCancel} variant="outline" className="flex-1">
                キャンセル
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {/* ツールバー */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">フリーモード テンプレート</h2>
        <Button onClick={handleNewTemplate} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          新規テンプレート
        </Button>
      </div>
      
      {/* テンプレートリスト */}
      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">📝</div>
            <p className="text-gray-600 mb-4">
              保存されたテンプレートがありません
            </p>
            <Button onClick={handleNewTemplate}>
              <Plus className="w-4 h-4 mr-2" />
              最初のテンプレートを作成
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{template.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {template.description && (
                  <p className="text-sm text-gray-600">{template.description}</p>
                )}
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">システムプロンプト:</div>
                  <div className="text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">
                    {template.systemPrompt}
                  </div>
                </div>
                
                <div className="text-xs text-gray-400">
                  更新: {new Date(template.updatedAt).toLocaleDateString()}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => onStartSession(template)}
                  >
                    <Play className="w-3 h-3 mr-1" />
                    このテンプレートでプレイ
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(template)}
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    編集
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm(`"${template.name}"を削除しますか？`)) {
                        onDelete(template.id)
                      }
                    }}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}